import { useState } from 'react';
import type { FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import type { AdminProductOffer, AdminProductOfferListParams } from '../../../api/types';
import { adminApi } from '../adminApi';
import { getAdminCatalogProductDetailPath, getAdminImportDetailPath } from '../adminRoutes';
import { CatalogPagination } from '../catalog/CatalogPagination';
import { CatalogRequestState } from '../catalog/CatalogRequestState';
import { catalogDate, catalogPrice, catalogQuantity } from '../catalog/catalogDisplay';

interface CompanySupplierOffersTabProps {
  companyId: number;
}

type AvailabilityFilter = '' | 'available' | 'unavailable';

export function CompanySupplierOffersTab({ companyId }: CompanySupplierOffersTabProps) {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [availability, setAvailability] = useState<AvailabilityFilter>('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [appliedAvailability, setAppliedAvailability] = useState<AvailabilityFilter>('');
  const productOffers = useQuery({
    queryKey: ['company', 'offers', companyId, page, appliedSearch, appliedAvailability],
    queryFn: () => adminApi.getProductOffers(createProductOfferParams(companyId, page, appliedSearch, appliedAvailability)),
  });

  function applyFilters(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setPage(1);
    setAppliedSearch(search.trim());
    setAppliedAvailability(availability);
  }

  function resetFilters(): void {
    setPage(1);
    setSearch('');
    setAvailability('');
    setAppliedSearch('');
    setAppliedAvailability('');
  }

  return (
    <section className="admin-detail-card" aria-labelledby="company-offers-title">
      <header className="catalog-section-heading">
        <div>
          <h2 id="company-offers-title">Предложения компании {productOffers.data?.meta.total !== undefined ? <span>{productOffers.data.meta.total}</span> : null}</h2>
          <p>Опубликованные товары и их актуальные условия поставки.</p>
        </div>
      </header>
      <form className="admin-filter-bar" onSubmit={applyFilters}>
        <label className="admin-field">
          <span>Поиск предложений</span>
          <input value={search} onChange={(event) => setSearch(event.target.value)} />
        </label>
        <label className="admin-field">
          <span>Наличие</span>
          <select value={availability} onChange={(event) => setAvailability(event.target.value as AvailabilityFilter)}>
            <option value="">Все предложения</option>
            <option value="available">В наличии</option>
            <option value="unavailable">Нет в наличии</option>
          </select>
        </label>
        <button className="catalog-button catalog-button_primary" type="submit">Найти</button>
        <button className="catalog-button" type="button" onClick={resetFilters}>Сбросить</button>
      </form>

      {productOffers.isPending || productOffers.isError ? (
        <CatalogRequestState error={productOffers.error?.message} onRetry={() => { void productOffers.refetch(); }} />
      ) : productOffers.data.items.length === 0 ? (
        <p className="admin-empty">Предложения не найдены.</p>
      ) : (
        <div className="catalog-data-scroll" tabIndex={0} aria-label="Предложения компании">
          <table className="catalog-data-table">
            <thead><tr><th>Товар</th><th>Позиция поставщика</th><th>Цена</th><th>Минимум</th><th>Наличие</th><th>Обновлено</th><th>Импорт</th></tr></thead>
            <tbody>{productOffers.data.items.map(renderProductOffer)}</tbody>
          </table>
        </div>
      )}
      {!productOffers.isPending && !productOffers.isError && productOffers.data.items.length > 0 ? (
        <CatalogPagination page={page} pageCount={productOffers.data.meta.page_count ?? 1} onChange={setPage} />
      ) : null}
    </section>
  );
}

function createProductOfferParams(companyId: number, page: number, search: string, availability: AvailabilityFilter): AdminProductOfferListParams {
  return {
    company_id: companyId,
    page,
    per_page: 20,
    search,
    offer_availability: availability === '' ? null : availability === 'available',
  };
}

function renderProductOffer(offer: AdminProductOffer) {
  return (
    <tr key={offer.offer_id}>
      <td><Link to={getAdminCatalogProductDetailPath(offer.product_id)}>{offer.product_name}</Link><small>{offer.product_sku}</small></td>
      <td>{offer.supplier_product_name || '—'}<small>{offer.supplier_product_sku}</small></td>
      <td className="catalog-price">{catalogPrice(offer.offer_price)}</td>
      <td>{catalogQuantity(offer.offer_min_quantity)}</td>
      <td><span className={offer.offer_availability ? 'catalog-stock is-available' : 'catalog-stock'}>{offer.offer_availability ? 'В наличии' : 'Нет в наличии'}</span></td>
      <td>{catalogDate(offer.offer_updated_at)}</td>
      <td>{offer.offer_import_id !== undefined && offer.offer_import_id !== null ? <Link to={getAdminImportDetailPath(offer.offer_import_id)}>Импорт №{offer.offer_import_id}</Link> : '—'}</td>
    </tr>
  );
}
